import { redirect } from "react-router";
import type { MiddlewareFunction } from "react-router";
import { NO_CONSOLE_PERMISSIONS_ERROR_MESSAGE } from "@/constants/authCopy";
import { AppStoreRouterContext } from "@/router/routerAppContext";
import type { AppStore } from "@/store/app/storeTypes";
import { getLandingPage } from "@/lib/permissions/getLandingPage";
import {
  firstAccessibleNavPath,
  isPathAllowed,
  normalizePath,
  parsePathAccess,
} from "@/lib/permissions/pathAccess";
import { getPermissions } from "@/lib/permissions/getPermissions";
import { requireFeaturesDataForReadySession } from "@/lib/session/requireFeaturesDataForReadySession";

export const routeAccessMiddleware: MiddlewareFunction = async (
  { request, context },
  next,
) => {
  let store: AppStore | undefined;
  try {
    store = context.get(AppStoreRouterContext);
  } catch {
    store = undefined;
  }
  if (store == null) {
    throw new Error(
      "routeAccessMiddleware: AppStore missing from router context. Pass store into createAppRouter(store).",
    );
  }

  const state = store.getState();
  const url = new URL(request.url);
  const featuresData = requireFeaturesDataForReadySession(
    state.user.userProfile,
    state.user.features.data,
  );

  if (featuresData === undefined) {
    await next();
    return;
  }
  const { permissionGrants } = getPermissions(
    state.user.userProfile,
    featuresData,
  );

  const { pathname } = url;
  const pathAccess = parsePathAccess(pathname);

  if (isPathAllowed(pathAccess, permissionGrants, featuresData)) {
    await next();
    return;
  }

  const landing = getLandingPage(permissionGrants);
  const fallback =
    landing.action === "redirect"
      ? landing.toPath
      : firstAccessibleNavPath(permissionGrants, featuresData);
  const normalizedCurrent = normalizePath(pathname);
  const normalizedFallback = fallback != null ? normalizePath(fallback) : null;

  if (
    fallback != null &&
    normalizedFallback != null &&
    normalizedFallback !== normalizedCurrent
  ) {
    return redirect(fallback);
  }

  if (fallback == null) {
    throw new Error(NO_CONSOLE_PERMISSIONS_ERROR_MESSAGE);
  }

  await next();
};
